/**
 * ops2026 UCP checkout test card — one row per site with a "Run UCP
 * checkout test" button. Clicking it POSTs
 * `/internal/admin/accounts/:account_id/sites/:site_id/ucp-checkout-test`
 * and renders the server's per-step result list.
 *
 * Lives in the Account's `Test` tab beside `SmokeOptInCard`. Light/dark
 * theme tokens via `useC`; no exclamation points, no emoji — matches the
 * admin's prevailing voice.
 */

import { useEffect, useRef, useState } from 'react';

import type {
  AdminAccountSite,
  AdminUcpCheckoutTestResponse,
  AdminUcpCheckoutTestStep,
  AdminUcpCheckoutTestStepStatus,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface UcpCheckoutTestCardProps {
  readonly accountId: string;
  readonly sites: readonly AdminAccountSite[];
}

type SiteState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  | {
      readonly kind: 'result';
      readonly result: AdminUcpCheckoutTestResponse;
      readonly finishing?: boolean;
      readonly finish_error?: string;
      /** Rejected / canceled / not found — stop auto-finishing. */
      readonly finish_stopped?: boolean;
    }
  | { readonly kind: 'error'; readonly message: string };

function stepTone(status: AdminUcpCheckoutTestStepStatus): PillTone {
  if (status === 'ok') return 'mint';
  if (status === 'skipped' || status === 'held') return 'amber';
  return 'coral';
}

function stepLabel(status: AdminUcpCheckoutTestStepStatus): string {
  if (status === 'ok') return 'OK';
  if (status === 'skipped') return 'Skipped';
  if (status === 'held') return 'Held';
  return 'Failed';
}

const AUTO_FINISH_POLL_MS = 10_000;
const AUTO_FINISH_MAX_MS = 60 * 60 * 1000;

// A held run survives a reload so the operator can approve it in the
// merchant dashboard and come back to finish it. Per-browser convenience.
const HELD_KEY = (site_id: string): string => `ucp-test-held:${site_id}`;

function readHeld(site_id: string): AdminUcpCheckoutTestResponse | null {
  try {
    const raw = window.localStorage.getItem(HELD_KEY(site_id));
    if (raw === null) return null;
    const v = JSON.parse(raw) as AdminUcpCheckoutTestResponse;
    return v.held === true && typeof v.session_id === 'string' ? v : null;
  } catch {
    return null;
  }
}

function writeHeld(site_id: string, result: AdminUcpCheckoutTestResponse | null): void {
  try {
    if (result === null || !result.held) window.localStorage.removeItem(HELD_KEY(site_id));
    else window.localStorage.setItem(HELD_KEY(site_id), JSON.stringify(result));
  } catch {
    // Storage unavailable — the held run just won't survive a reload.
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: unknown } | undefined;
    if (typeof body?.message === 'string') return body.message;
    if (err.kind === 'rate_limited') return 'Rate limited: 1 run per minute, 20 per day, per site.';
    if (err.server_reason !== undefined) return `Request failed: ${err.server_reason}.`;
  }
  return err instanceof Error ? err.message : 'Request failed.';
}

export function UcpCheckoutTestCard({
  accountId,
  sites,
}: UcpCheckoutTestCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [state, setState] = useState<Record<string, SiteState>>(() => {
    const init: Record<string, SiteState> = {};
    for (const site of sites) {
      const held = readHeld(site.site_id);
      if (held !== null) init[site.site_id] = { kind: 'result', result: held };
    }
    return init;
  });

  // The test issues real Stripe TEST charges + refunds, so only
  // non-deleted active-ish sites get a button. Suspended / deactivated
  // sites render as rows with disabled buttons so the staff can see
  // them but not fire the test.
  const eligibleSites = sites.filter((s) => s.status !== 'deactivated');
  // ADR-0254 — optional promotion code from the merchant's Stripe, applied to the next run.
  const [discountCode, setDiscountCode] = useState('');
  const codeValid = discountCode.trim() === '' || /^[A-Za-z0-9_-]{1,64}$/.test(discountCode.trim());

  const runOne = async (site: AdminAccountSite): Promise<void> => {
    setState((prev) => ({ ...prev, [site.site_id]: { kind: 'running' } }));
    try {
      const code = discountCode.trim();
      const result = code.length > 0
        ? await api.runUcpCheckoutTest(accountId, site.site_id, { discount_code: code })
        : await api.runUcpCheckoutTest(accountId, site.site_id);
      writeHeld(site.site_id, result);
      setState((prev) => ({
        ...prev,
        [site.site_id]: { kind: 'result', result },
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        [site.site_id]: { kind: 'error', message: errorMessage(err) },
      }));
    }
  };

  // Second half of a held run: the merchant approved it in Held orders,
  // so send the platform's second complete + refund. The new steps are
  // appended under the held run so the whole story reads top to bottom.
  const finishOne = async (
    site: AdminAccountSite,
    prior: AdminUcpCheckoutTestResponse,
    opts: { readonly quiet?: boolean } = {},
  ): Promise<void> => {
    if (prior.session_id === null) return;
    setState((prev) => ({
      ...prev,
      [site.site_id]: {
        kind: 'result',
        result: prior,
        finishing: true,
        // A background check keeps the current note instead of flashing.
        ...(opts.quiet === true && prev[site.site_id]?.kind === 'result'
          ? { finish_error: (prev[site.site_id] as { finish_error?: string }).finish_error }
          : {}),
      } as SiteState,
    }));
    try {
      const next = await api.finishUcpCheckoutTest(accountId, site.site_id, {
        run_id: prior.run_id,
        session_id: prior.session_id,
      });
      const merged: AdminUcpCheckoutTestResponse = {
        ...next,
        steps: [...prior.steps, ...next.steps.map((st) => ({ ...st, label: `${st.label} (after approval)` }))],
      };
      writeHeld(site.site_id, null);
      setState((prev) => ({
        ...prev,
        [site.site_id]: { kind: 'result', result: merged },
      }));
    } catch (err) {
      const message = errorMessage(err);
      const still_held = err instanceof ApiError && err.status === 409 && /Still held/.test(message);
      // A rejected / already-finished order can't be finished; forget it.
      const stopped = err instanceof ApiError
        && (err.status === 404 || /rejected|already charged|canceled/i.test(message));
      if (stopped) writeHeld(site.site_id, null);
      setState((prev) => ({
        ...prev,
        [site.site_id]: {
          kind: 'result',
          result: prior,
          // While still held, the panel already says it is waiting.
          ...(still_held ? {} : { finish_error: message }),
          ...(stopped ? { finish_stopped: true } : {}),
        },
      }));
    }
  };

  // Auto-finish: while a run is held, ask the server every 10 s whether the
  // merchant approved it. The finish route answers 409 (no Stripe call, no
  // rate-limit budget used) until the order is approved, then charges and
  // refunds — the same hand-off a real agent platform makes on its own.
  const stateRef = useRef(state);
  stateRef.current = state;
  const finishRef = useRef(finishOne);
  finishRef.current = finishOne;
  useEffect(() => {
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > AUTO_FINISH_MAX_MS) return;
      for (const site of sites) {
        const st = stateRef.current[site.site_id];
        if (
          st?.kind === 'result'
          && st.result.held
          && st.finishing !== true
          && st.finish_stopped !== true
        ) {
          void finishRef.current(site, st.result, { quiet: true });
        }
      }
    }, AUTO_FINISH_POLL_MS);
    return () => window.clearInterval(id);
  }, [sites]);

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          UCP checkout test
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            letterSpacing: 1,
          }}
        >
          Test-mode Stripe only
        </div>
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        Runs the full UCP checkout path end to end against a site: setup
        check, catalog pick, create, shipping (if the site has it), complete
        with <span style={{ fontFamily: monoFont, color: C.text }}>tok_visa</span>,
        then refund the Stripe charge. If the site's rules hold the order,
        approve it in Held orders and the run finishes on its own. Live-mode
        sites are refused before any charge. 1 run per minute, 20 per day,
        per site.
      </div>
      <label style={{ display: 'block', marginBottom: 12 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>DISCOUNT CODE (OPTIONAL)</div>
        <input
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          placeholder="A promotion code from the merchant's Stripe"
          maxLength={64}
          data-testid="ucp-test-discount-code"
          style={{ width: '100%', maxWidth: 320, padding: '8px 10px', background: 'transparent', border: `1px solid ${codeValid ? C.border : C.coral}`, borderRadius: 2, color: C.text, fontFamily: monoFont, fontSize: 12 }}
        />
        {!codeValid ? <div style={{ fontFamily: sansFont, fontSize: 11, color: C.coral, marginTop: 4 }}>Letters, digits, - and _ only.</div> : null}
      </label>

      {eligibleSites.length === 0 ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          This account has no active sites.
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-2 list-none p-0 m-0">
          {eligibleSites.map((site) => {
            const row = state[site.site_id] ?? { kind: 'idle' };
            return (
              <SiteRow
                key={site.site_id}
                site={site}
                state={row}
                onRun={() => void runOne(site)}
                onFinish={(prior) => void finishOne(site, prior)}
              />
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function SiteRow({
  site,
  state,
  onRun,
  onFinish,
}: {
  site: AdminAccountSite;
  state: SiteState;
  onRun: () => void;
  onFinish: (prior: AdminUcpCheckoutTestResponse) => void;
}): JSX.Element {
  const C = useC();
  const running = state.kind === 'running' || (state.kind === 'result' && state.finishing === true);
  return (
    <li
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: '12px 14px',
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
              wordBreak: 'break-all',
            }}
            title={site.domain}
          >
            {site.domain}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.dim,
              marginTop: 2,
              wordBreak: 'break-all',
            }}
          >
            {site.site_id}
          </div>
        </div>
        <button
          type="button"
          disabled={running}
          onClick={onRun}
          style={{
            background: 'transparent',
            color: C.text,
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 500,
            padding: '10px 16px',
            borderRadius: 2,
            border: `1px solid ${C.border}`,
            cursor: running ? 'not-allowed' : 'pointer',
            opacity: running ? 0.6 : 1,
            minHeight: 40,
          }}
        >
          {running ? 'Running…' : 'Run UCP checkout test'}
        </button>
      </div>
      {state.kind === 'error' ? (
        <div
          style={{
            marginTop: 10,
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
          }}
        >
          {state.message}
        </div>
      ) : null}
      {state.kind === 'result' ? <ResultPanel result={state.result} /> : null}
      {state.kind === 'result' && state.result.held && state.finish_stopped !== true ? (
        <div
          className="flex items-center justify-between gap-3 flex-wrap"
          style={{
            marginTop: 10,
            padding: '10px 12px',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5, flex: '1 1 240px' }}>
            Waiting for approval. Approve the SILL TEST order in the
            merchant's Held orders and this run finishes on its own within
            about 10 seconds: it sends the second complete, charges the test
            card and refunds it. Finish now checks immediately.
            {state.finish_error !== undefined ? (
              <div style={{ color: C.coralFg, marginTop: 6 }}>{state.finish_error}</div>
            ) : null}
          </div>
          <button
            type="button"
            disabled={state.finishing === true}
            onClick={() => onFinish(state.result)}
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 500,
              padding: '10px 16px',
              borderRadius: 2,
              border: 'none',
              cursor: state.finishing === true ? 'not-allowed' : 'pointer',
              opacity: state.finishing === true ? 0.6 : 1,
              minHeight: 40,
            }}
          >
            {state.finishing === true ? 'Checking…' : 'Finish now'}
          </button>
        </div>
      ) : null}
    </li>
  );
}

function ResultPanel({
  result,
}: {
  result: AdminUcpCheckoutTestResponse;
}): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        marginTop: 12,
        borderTop: `1px solid ${C.borderSoft}`,
        paddingTop: 10,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Run {result.run_id}
      </div>
      <ol
        className="list-none p-0 m-0 grid grid-cols-1 gap-2"
      >
        {result.steps.map((step, i) => (
          <StepRow key={`${step.id}-${i}`} step={step} />
        ))}
      </ol>
      <ResultIds result={result} />
    </div>
  );
}

function StepRow({ step }: { step: AdminUcpCheckoutTestStep }): JSX.Element {
  const C = useC();
  return (
    <li
      className="flex items-start justify-between gap-3"
      style={{
        padding: '8px 10px',
        background: C.surface,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <div className="min-w-0 flex-1">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
            marginBottom: 2,
          }}
        >
          {step.label}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {step.detail}
        </div>
      </div>
      <Pill tone={stepTone(step.status)}>{stepLabel(step.status)}</Pill>
    </li>
  );
}

function ResultIds({
  result,
}: {
  result: AdminUcpCheckoutTestResponse;
}): JSX.Element | null {
  const C = useC();
  const items: { label: string; value: string }[] = [];
  if (result.session_id !== null) items.push({ label: 'Session', value: result.session_id });
  if (result.order_id !== null) items.push({ label: 'Order', value: result.order_id });
  if (result.refund_id !== null) items.push({ label: 'Refund', value: result.refund_id });
  if (items.length === 0) return null;
  return (
    <div
      className="flex items-center flex-wrap"
      style={{
        marginTop: 10,
        gap: 14,
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
      }}
    >
      {items.map((it) => (
        <span key={it.label} className="flex items-baseline gap-1">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
            }}
          >
            {it.label}
          </span>
          <span style={{ color: C.body, wordBreak: 'break-all' }}>{it.value}</span>
        </span>
      ))}
    </div>
  );
}
